import type { GtagEvent, Slot } from '../../types/assertive'
import useOutbrainAds from '../../composables/ad-ops/useOutbrainAds'
import debug from '../console'

const logger = debug.withTag('adFunctions')

export function stripAYCode(placement: string): string {
  // Strip away "__ayManagerEnv__XX..." suffix off the element ID to get the placement class
  return placement.replace(/__ayManagerEnv__.+$/, '')
}

export function trackAssertiveRender(placement: string, adUnitPath: string) {
  const { track } = useAnalytics()
  const eventData: NtpAdEvent = {
    // ntp_tile_position: props.tilePosition,
    ntp_ad_brand: 'programmatic',
    ntp_ad_provider: 'programmatic',
    ntp_creative_id: adUnitPath,
    ntp_placement_name: placement,
    ntp_ad_refresh: false,
  }
  track('NtpAd_Rendered', eventData)
}

/**
 * Handles rendering an Outbrain native ad as a fallback when an Adx slot is empty and records analytics for filled slots.
 *
 * If the placement element cannot be located the function logs an error and returns.
 * When the provided event indicates an empty Adx slot, the function destroys the Adx instance for that element and renders the Outbrain fallback into the placement.
 * If the slot is filled, the function emits the assertive-render analytics event.
 *
 * @param event - Gtag slot-render event whose `isEmpty` property indicates whether the Adx slot returned no creative and whose `slot` provides the slot's identifiers
 */
export function setupNativeAdAsFallback(event: GtagEvent) {
  const elementId = event.slot.getSlotElementId() // Element id for Adx script
  const placementElement = document.getElementById(elementId)?.parentElement
  const placementClass = stripAYCode(elementId)

  if (!placementElement) {
    logger.error('Element not found for native ad fallback', placementClass)
    return
  }

  if (event.isEmpty) {
    try {
      const { renderFallbackOutbrainAd } = useOutbrainAds()
      window.ayManagerEnv.cmd.push(function () {
        window.ayManagerEnv.destroy([elementId], {
          useInstanceCodes: true,
        })
      })
      renderFallbackOutbrainAd(placementClass, placementElement)
      return
    } catch (err) {
      logger.error(
        '[Native-Fallback]: Error on Gtag slotRenderEnded listener for native ad fallback',
        err
      )
    }
  }

  trackAssertiveRender(placementClass, event.slot.getAdUnitPath())
}

export function getFallbackPlacementWidget(
  placement: string
): OutbrainProps | undefined {
  const placementConfigs = useAppConfig().adops?.placements

  if (!placementConfigs) {
    logger.error('No outbrain widgets found')
    return
  }

  if (!placementConfigs || !placementConfigs[placement]?.outbrain) {
    console.error('No outbrain widget found for: ', placement)
    return
  }

  // return regular placement name so its targeted for removal in classList
  return { ...placementConfigs[placement].outbrain, fallback: true, placement }
}

// This function is used to get the Outbrain widget ID for a specific placement.
// Used to set the dynamic price for the placement.
export function getOutbrainWidgetIdForPlacement(
  placement: string
): string | undefined {
  const widgets = useAppConfig().adops?.placements

  if (!widgets) {
    logger.error('No mapping for placement to outbrain widgets found.')
    return
  }

  if (!widgets[placement] || !widgets[placement].outbrain?.widgetId) {
    logger.error('No outbrain widget found for: ', placement)
    return
  }

  return widgets[placement].outbrain.widgetId
}

export function getIndexOfPlacement(
  placement: string,
  el: Element | null
): number {
  if (!el || !placement) {
    logger.error('Element is null for placement:', placement)
    return 0
  }

  const placementDivs = Array.from(
    document.querySelectorAll(`.${placement}`)
  ).filter((div) => {
    return div.hasAttribute('ad')
  })

  if (placementDivs.length > 1 && el) {
    const index = Array.from(placementDivs).findIndex((div) => div === el)
    if (index !== -1) {
      return index
    }
  }
  return 0
}

export function handleStickyAd(placementDiv: Element, timeout = 3000): void {
  if (!placementDiv) {
    logger.error('No element to handle sticky ad')
    return
  }

  placementDiv.classList.add('sticky')
  placementDiv.classList.add('top-0')
  placementDiv.classList.add('z-[1000]')

  // delay the timer until scroll not load
  let scrollTimeout: ReturnType<typeof setTimeout>
  window.addEventListener('scroll', () => {
    if (!placementDiv.classList.contains('sticky')) {
      clearTimeout(scrollTimeout)
    }
    scrollTimeout = setTimeout(() => {
      placementDiv.classList.remove('sticky')
      placementDiv.classList.remove('top-0')
      placementDiv.classList.remove('z-[1000]')
    }, timeout)
  })
}

export function updateSlotTargeting(adUnitInstanceCode: string, slot: Slot) {
  const placement = stripAYCode(adUnitInstanceCode)

  slot.updateTargetingFromMap({
    placement,
  })
}
